/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2025 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { FC, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import { ExamCodeFieldProps } from "../../../types/rule";
import { getSelectStyles, IN, NOT_IN, EXAM_CODE } from "../../../constant";
import debounce from "lodash.debounce";
import { SelectChangeFunction } from "../../../types/components/reactSelectSearchProps";
import { ICodeResponse } from "../../../types/order";
import axios from "axios";
import { OptionTypeBase } from "../../../types/common";
import AHSAsyncSelect from "../../Select/AHSAsyncSelect";

const ExamCode: FC<ExamCodeFieldProps> = ({
  disabled,
  id,
  parent,
  errors,
  codes,
  operator,
  onValueChangeHandler,
  isDark,
  options,
}: ExamCodeFieldProps) => {
  const { t } = useTranslation("rule");
  const [selectedOperator, setSelectedOperator] = useState(null);
  const [selectedOptions, setSelectedOptions] = useState(null);
  const [allCodesOptions, setAllCodesOptions] = useState<OptionTypeBase[]>(
    options?.map((x) => {
      return {
        label: x.name,
        value: x.id,
      };
    })
  );

  const getCodes = async (params: {
    keyword: string;
    mode: string;
  }): Promise<Array<ICodeResponse>> => {
    try {
      const rs = await axios.get("/codeSystems/codes", { params });
      return rs.data.content;
    } catch {
      return [];
    }
  };

  const fetchCodes = (
    query: string,
    callback: (options: OptionTypeBase[]) => void
  ) => {
    if (query.length > 2) {
      getCodes({
        keyword: query,
        mode: "dropdown",
      }).then((rs: ICodeResponse[]) => {
        callback(
          rs.map((r) => {
            return {
              label: `${r.code} - ${r.display}`,
              value: `${r.id}`,
            };
          })
        );
      });
      return;
    }
    callback([]);
  };

  const debounceFetchCodes = debounce(fetchCodes, 500);

  useEffect(() => {
    if (allCodesOptions?.length > 0) {
      setSelectedOptions(
        allCodesOptions.filter((item) => codes?.includes(item.value))
      );
    }
  }, [allCodesOptions, codes]);

  useEffect(() => {
    setSelectedOperator(operator);
  }, [operator]);

  const dispatchNewValue = (data: string[], newOperator: string) => {
    onValueChangeHandler(
      {
        scope: "conditions",
        id,
        type: EXAM_CODE,
        stringCollectionProperty: {
          property: "examCode",
          operator: newOperator,
          values: data,
        },
      },
      parent
    );
  };

  const onSelectChange: SelectChangeFunction = (selectedItems) => {
    setSelectedOptions(selectedItems);
    const curIds = allCodesOptions?.map((item) => item.value);
    const allCodesOptionsNew = [
      ...allCodesOptions,
      ...selectedItems.filter((item) => !curIds.includes(item.value)),
    ];
    setAllCodesOptions(allCodesOptionsNew);

    dispatchNewValue(
      selectedItems.map((item: { value: any }) => item.value),
      operator
    );
  };

  const selectOperator = (update: any) => {
    if ([IN, NOT_IN].includes(update?.value)) {
      dispatchNewValue(
        selectedOptions?.map((item) => item.value),
        update?.value
      );
    }
  };

  const operatorOptions = useMemo(() => {
    const result: Array<OptionTypeBase> = [
      {
        label: `${t("in_")}`,
        value: IN,
      },
      {
        label: `${t("not_in")}`,
        value: NOT_IN,
      },
    ];
    return result;
  }, [t]);

  const hasError = () => {
    return (
      errors?.find((item) => item.id === id && item.type === EXAM_CODE) !==
      undefined
    );
  };

  return (
    <div className="row">
      <div className="mb-1 col col-md-3">
        <Select
          styles={getSelectStyles(isDark)}
          menuPlacement="auto"
          menuPosition="fixed"
          isDisabled={disabled}
          id={`${id}_operator`}
          name="operator"
          classNamePrefix="form-control-select"
          options={operatorOptions}
          placeholder={t("select_operator")}
          onChange={selectOperator}
          value={operatorOptions?.find((option) => {
            return option?.value === selectedOperator;
          })}
        />
        {hasError() && !selectedOperator && (
          <label
            className="validation-invalid-label"
            htmlFor={`${id}_operator`}
          >
            {t("field_required")}
          </label>
        )}
      </div>

      <div className="mb-1 col col-md-5">
        <AHSAsyncSelect
          styles={getSelectStyles(isDark)}
          isDark={isDark}
          menuPlacement="auto"
          menuPosition="fixed"
          isDisabled={disabled}
          id={`${id}_operator`}
          name="operator"
          classNamePrefix="form-control-select"
          loadOptions={debounceFetchCodes}
          defaultOptions
          onChange={onSelectChange}
          placeholder={
            <span className="p-l-xxs w-100">
              {t("form:document.type_to_search")}
            </span>
          }
          noOptionsMessage={() => null}
          value={selectedOptions}
          inputId="universalServiceIdentifierOption"
          isClearable
          isMulti
        />
        {hasError() && (!selectedOptions || selectedOptions?.length == 0) && (
          <label
            className="validation-invalid-label"
            htmlFor={`${id}_exam_code_field`}
          >
            {t("form:hl7.field_required")}
          </label>
        )}
      </div>
    </div>
  );
};

export default ExamCode;
